'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Card, CardBody, Button, Progress, useDisclosure } from '@heroui/react';
import { Icon } from '@iconify/react';
import clientAccService from '../../../../api/client/clientAccService';
import IconInfo from './icon-info.svg';
import IconUpload from './upload-ic.svg';
import IcClose from './close.svg';
import AccCreatedModal from '../components/AccCreatedModal';
import { useTranslations } from '../../../../contexts/translation.context';
interface Step3ContentUploadProps {
  modelId?: string;
  onUploadComplete?: () => void;
  onAddMoreFiles?: React.MutableRefObject<(() => void) | null>;
  onFilesCountChange?: (count: number) => void;
  onStartUpload?: React.MutableRefObject<(() => void) | null>;
  onValidationError?: (errors: string[]) => void;
  onModalClose?: () => void;
}

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  isUploading: boolean;
  uploadProgress: number;
  error?: string;
}

const Step3ContentUpload: React.FC<Step3ContentUploadProps> = ({
  modelId,
  onUploadComplete,
  onAddMoreFiles,
  onFilesCountChange,
  onStartUpload,
  onValidationError,
  onModalClose,
}) => {
  const { t } = useTranslations();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Функция для открытия диалога выбора файлов
  const openFileDialog = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Экспортируем функцию через пропс
  useEffect(() => {
    if (onAddMoreFiles) {
      onAddMoreFiles.current = openFileDialog;
    }
  }, [onAddMoreFiles, openFileDialog]);

  // Отслеживаем изменение количества файлов
  useEffect(() => {
    if (onFilesCountChange) {
      onFilesCountChange(uploadedFiles.length);
    }
  }, [uploadedFiles.length, onFilesCountChange]);

  // Функция для начала загрузки всех файлов
  const startUploadAllFiles = useCallback(() => {
    const filesToUpload = uploadedFiles.filter(
      (file) => !file.isUploading && file.uploadProgress === 0
    );
    filesToUpload.forEach((file) => uploadFile(file));
  }, [uploadedFiles]);

  // Экспортируем функцию через пропс
  useEffect(() => {
    if (onStartUpload) {
      onStartUpload.current = startUploadAllFiles;
    }
  }, [onStartUpload, startUploadAllFiles]);

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files) return;

      const maxFileSize = 5 * 1024 * 1024; // 5MB
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const errors: string[] = [];

      const validFiles = Array.from(files).filter((file) => {
        if (!allowedTypes.includes(file.type)) {
          errors.push(t('createAccount.step3.errors.unsupportedFormat', { fileName: file.name }));
          return false;
        }
        if (file.size > maxFileSize) {
          errors.push(
            t('createAccount.step3.errors.fileTooLarge', {
              fileName: file.name,
              size: (file.size / 1024 / 1024).toFixed(1),
            })
          );
          return false;
        }
        return true;
      });

      // Check if adding these files would exceed the limit
      if (uploadedFiles.length + validFiles.length > 6) {
        errors.push(t('createAccount.step3.errors.maxFiles'));
      }

      // Показываем ошибки если есть
      if (errors.length > 0) {
        setValidationErrors(errors);
        // Передаем ошибки в родительский компонент
        onValidationError?.(errors);
        // Очищаем ошибки через 5 секунд
        setTimeout(() => {
          setValidationErrors([]);
          onValidationError?.([]);
        }, 5000);
        return;
      }

      // Очищаем ошибки если все файлы валидны
      setValidationErrors([]);
      onValidationError?.([]);

      const newFiles: UploadedFile[] = validFiles.map((file) => ({
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(file),
        isUploading: false,
        uploadProgress: 0,
      }));

      setUploadedFiles((prev) => [...prev, ...newFiles]);

      // Не загружаем файлы автоматически - только добавляем в список
      // newFiles.forEach(file => uploadFile(file));
    },
    [uploadedFiles.length]
  );

  const uploadFile = async (fileData: UploadedFile) => {
    if (!modelId) {
      console.error('ModelId не найден для загрузки файла');
      return;
    }

    setUploadedFiles((prev) =>
      prev.map((f) =>
        f.id === fileData.id ? { ...f, isUploading: true, uploadProgress: 0, error: undefined } : f
      )
    );

    let progressInterval: NodeJS.Timeout | undefined;

    try {
      const formData = new FormData();
      formData.append('file', fileData.file);

      // Simulate upload progress
      progressInterval = setInterval(() => {
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === fileData.id ? { ...f, uploadProgress: Math.min(f.uploadProgress + 10, 90) } : f
          )
        );
      }, 200);

      // Отправляем файл через API
      const response = await clientAccService.publicMedia(modelId, formData);
      console.log('Файл успешно загружен:', response);

      clearInterval(progressInterval);

      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id ? { ...f, isUploading: false, uploadProgress: 100 } : f
        )
      );

      // Проверяем, все ли файлы загружены
      setUploadedFiles((prev) => {
        const allUploaded = prev.every((f) => f.uploadProgress === 100 || f.error);
        if (allUploaded && prev.length >= 3) {
          // Вызываем onUploadComplete только когда все файлы загружены и их минимум 3
          setTimeout(() => {
            onUploadComplete?.();
            // Показываем модалку успеха
            onOpen();
          }, 100);
        }
        return prev;
      });
    } catch (error: any) {
      console.error('Ошибка при загрузке файла:', error);

      if (progressInterval) {
        clearInterval(progressInterval);
      }

      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.id === fileData.id
            ? {
                ...f,
                isUploading: false,
                error: error?.response?.data?.detail || t('createAccount.step3.errors.uploadError'),
              }
            : f
        )
      );
    }
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles((prev) => {
      const file = prev.find((f) => f.id === fileId);
      if (file) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((f) => f.id !== fileId);
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
    if (e.target) {
      e.target.value = '';
    }
  };

  const canUploadMore = uploadedFiles.length < 6;

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      uploadedFiles.forEach((file) => {
        URL.revokeObjectURL(file.preview);
      });
    };
  }, []);

  return (
    <div className="space-y-4">
      {/* Information Block */}
      <Card className="border border-l-[0.1875rem] border-l-partner rounded-none  shadow-sm bg-gray-98">
        <CardBody className="p-4 ">
          <div className="flex items-start gap-4">
            <div className="icon size-8 md:size-9">
              <img src={IconInfo} alt="" />
            </div>
            <div className="max-w-[32.5rem]">
              <h3 className="text-sm font-semibold text-partner">
                {t('createAccount.step3.contentFormat.title')}
              </h3>
              <div className="flex flex-col gap-2">
                <p className="text-sm text-auth-muted leading-5 mb-2">
                  {t('createAccount.step3.contentFormat.description1')}
                </p>
                <p className="text-sm text-auth-muted leading-5">
                  {t('createAccount.step3.contentFormat.description2')}
                </p>
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* File Upload Block */}
      <Card className="border border-gray-90  bg-gray-98 rounded-xl h-[8.25rem] shadow-sm">
        <CardBody className="p-4 overflow-hidden ">
          {/* Upload Area */}
          <div>
            {/* Drag & Drop Area */}

            <div
              className={`absolute text-center w-full h-full top-0 left-0 flex items-center justify-center flex-col gap-3 transition-colors ${
                isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {uploadedFiles.length === 0 && (
                <>
                  <div className="flex justify-center">
                    <img src={IconUpload} className="w-10 h-10 text-gray-400" />
                  </div>
                  <div>
                    <p className="text-sm font-regular text-auth-muted">
                      <span
                        style={{ cursor: 'pointer' }}
                        onClick={() => fileInputRef.current?.click()}
                        className="text-partner text-sm leading-5 font-semibold"
                      >
                        {t('createAccount.step3.upload.uploadText')}&nbsp;
                      </span>
                      {t('createAccount.step3.upload.orDragText')}
                    </p>
                    {/* <Button
                      color="primary"
                      variant="flat"
                      size="sm"
                      onPress={() => fileInputRef.current?.click()}
                      isDisabled={!canUploadMore}
                      className="mt-2"
                    >
                      Выберите файлы
                    </Button> */}
                  </div>
                </>
              )}
              {/* <p className="text-xs text-gray-500">
                    Поддерживаемые форматы: JPG, PNG. Максимум 6 файлов по 5MB каждый.
                  </p> */}
            </div>
            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileInputChange}
              className="hidden"
            />
          </div>

          {/* Uploaded Files Preview */}
          {uploadedFiles.length > 0 && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {uploadedFiles.map((file) => (
                  <div key={file.id} className="relative group size-[6.25rem]">
                    <div className="relative size-[6.25rem] aspect-square rounded-xl overflow-hidden bg-gray-100">
                      <img
                        src={file.preview}
                        alt="Preview"
                        className="size-[6.25rem] object-cover"
                      />

                      {/* Upload Progress Overlay */}
                      {file.isUploading && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                          <div className="text-center">
                            <Progress
                              value={file.uploadProgress}
                              className="w-16 h-2"
                              color="primary"
                            />
                            <p className="text-xs text-white mt-1">{file.uploadProgress}%</p>
                          </div>
                        </div>
                      )}

                      {/* Error Overlay */}
                      {file.error && (
                        <div className="absolute inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center">
                          <div className="text-center">
                            <Icon
                              icon="mdi:alert-circle"
                              className="w-6 h-6 text-white mx-auto mb-1"
                            />
                            <p className="text-xs text-white">{file.error}</p>
                          </div>
                        </div>
                      )}

                      {/* Success Overlay */}
                      {/* {!file.isUploading && !file.error && file.uploadProgress === 100 && (
                          <div className="absolute top-2 right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                            <Icon icon="mdi:check" className="w-4 h-4 text-white" />
                          </div>
                        )} */}
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeFile(file.id)}
                      className="absolute -top-[0.3125rem] -right-2.5 w-5 h-5 rounded-full flex items-center justify-center "
                    >
                      <img src={IcClose} className="w-5 h-5 text-white" />
                    </button>

                    {/* File Name */}
                    {/* <p className="text-xs text-gray-500 mt-1 truncate">
                        {file.file.name}
                      </p> */}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardBody>
      </Card>
      <AccCreatedModal
        isOpen={isOpen}
        onClose={() => {
          onClose();
          // Вызываем обработчик закрытия модалки
          onModalClose?.();
        }}
        accountName={modelId}
      />
    </div>
  );
};

export default Step3ContentUpload;
